import { classMap } from 'lit-html/directives/class-map.js';
import { nothing } from 'lit-html';
import { html } from 'lit-element';
import { KatLitElement, register } from '../../shared/base';
import { checkSlots } from '../../shared/slot-utils';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './alert.lit.scss';

const VARIANT_TO_ICON_NAME = {
  success: 'success',
  danger: 'danger',
  warning: 'warning',
  info: 'info-circle',
};

/**
 * @component {kat-alert} KatalAlert An alert helps the user know when they need to pay attention to a relatively small piece of information or when they need to take a special action.
 * @guideline Do For readability, maximum line width: 50-75 characters
 * @guideline Do For accessibility, always give the message in at least 2 of these 3 ways: color, text, icon
 * @guideline Do Convey the severity and urgency within the context of activity.
 * @guideline Do The title should be specific and informative enough that the customer understands what the issue is before they read any text below the title.
 * @guideline Dont It is not recommended to use multiple alerts in one page. If your rare use case requires this, stack the alerts ordered by the urgency with which the customers need to pay attention: error, warning, information, then success.
 * @event dismiss This event is fired whenever the alert is dismissed
 * @status Production
 * @theme flo
 * @contentProvided
 * @slot default Contents will be used as the description section of the alert.
 * @slot header Contents will be used as the header section of the alert.
 * @example Info {"header": "Informational Alert", "description": "Used to give users context.", "variant":"info"}
 * @example Success {"header": "Success Alert", "description": "Used to let users know an action was successful.", "variant":"success"}
 * @example Danger {"header": "Danger Alert", "description": "Can be used to let users know an error has occured.", "variant":"danger"}
 * @example Warning {"header": "Warning Alert", "description": "Used to let users know they should be careful with an action they are to perform", "variant":"warning"}
 * @example Slots {"content": "<span slot=\"header\">Header set via <i>slot</i></span>Description set with default slot, can contain <b>arbitrary</b> html, like <a href=\"http://www.amazon.com\">links</a>."}
 */
@register('kat-alert')
export class KatalAlert extends KatLitElement {
  static get properties() {
    return {
      /**
       * @required
       * @classprop {string} header This is the main text that will be displayed
       */
      header: { type: String, reflect: true },

      /**
       * @classprop {string} description This is the secondary text that will be displayed
       */
      description: { type: String, reflect: true },

      /**
       * @classprop {boolean} dismissed Whether or not the alert is dismissed - Default is false
       */
      dismissed: { type: Boolean, reflect: true },

      /**
       * @classprop {boolean} persistent Whether the alert can be dismissed. When true, the alert will render without a close button. Defaults to false.
       */
      persistent: {
        type: Boolean,
        reflect: true,
      },

      /**
       * @classprop {enum} variant This is the state of the alert - Default is info
       * @enum {value} info Info alert - Default
       * @enum {value} success Success alert
       * @enum {value} danger Danger alert
       * @enum {value} warning Warning alert
       */
      variant: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [baseStyles, styles];
  }

  constructor() {
    super();
    this.variant = 'info';
  }

  _dismissAlert() {
    this.dismissed = true;
    this.dispatchCustomEvent('dismiss', { dismissed: this.dismissed }, false);
  }

  /**
   * The icon is large if both the header & description are set, otherwise it is
   * small.
   */
  _getIconSize() {
    const slots = checkSlots(this);
    const hasDescription = slots.default || this.description;
    const hasHeader = slots.header || this.header;
    return hasDescription && hasHeader ? 'large' : 'small';
  }

  _childrenChanged() {
    this.requestUpdate();
  }

  render() {
    const size = this._getIconSize();
    const slots = checkSlots(this);
    const headerClasses = {
      empty: !(this.header || slots.header),
      header: true,
    };
    const descriptionClasses = {
      empty: !(this.description || slots.default),
      description: true,
    };
    this.setAttribute('size', size);
    return html`
      <kat-icon
        class="icon"
        name="${VARIANT_TO_ICON_NAME[this.variant || 'info']}"
        size="${size}"
      ></kat-icon>

      <div class="content">
        <div class="${classMap(headerClasses)}">
          <slot name="header" @slotchange=${this._childrenChanged}
            >${this.header}</slot
          >
        </div>
        <div class="${classMap(descriptionClasses)}">
          <slot @slotchange=${this._childrenChanged}>${this.description}</slot>
        </div>
      </div>

      ${this.persistent
        ? nothing
        : html`
            <kat-icon
              class="dismiss"
              name="exit"
              size="small"
              part="alert-dismiss-button"
              @click=${this._dismissAlert}
            ></kat-icon>
          `}
    `;
  }
}
